/**
 * Formulaire partagé — saisie + confirmation de mot de passe.
 *
 * Sécurité (Chunk 7.C) :
 *  - state local React uniquement (pas de localStorage/sessionStorage)
 *  - vidé immédiatement après submit (succès ET erreur)
 *  - pas de console.*, pas de toast avec echo password, pas d'appel API
 *  - submit délègue à `onSubmit` qui doit appeler supabase.auth.updateUser
 *
 * Le composant ne connaît pas Supabase — il valide localement et appelle
 * `onSubmit(password)`. La page parente est responsable de l'appel
 * `supabase.auth.updateUser({ password })`.
 */

import { type FormEvent, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  PASSWORD_MIN_LENGTH,
  validatePassword,
  type PasswordError,
} from "../../lib/password-policy";

type Props = {
  /** Email connu (pour vérification ≠ email). Optionnel. */
  email?: string;
  /** Libellé du bouton de soumission (i18n côté parent). */
  submitLabel: string;
  /** Libellé pendant submit (i18n côté parent). */
  submittingLabel: string;
  /** Callback submit. Reçoit le password déjà trimmé et validé. */
  onSubmit: (password: string) => Promise<void>;
  /** Désactive le formulaire (lien invalide / session non établie). */
  disabled?: boolean;
};

export function AuthPasswordForm({
  email,
  submitLabel,
  submittingLabel,
  onSubmit,
  disabled = false,
}: Props) {
  const { t } = useTranslation();
  const pwdId = useId();
  const confirmId = useId();
  const helpId = useId();
  const errorId = useId();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<PasswordError[]>([]);
  const [mismatch, setMismatch] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function clear() {
    setPassword("");
    setConfirm("");
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (disabled || submitting) return;

    const trimmed = password.trim();
    const v = validatePassword(trimmed, email);
    const isMismatch = trimmed !== confirm.trim();

    setErrors(v.ok ? [] : v.errors);
    setMismatch(isMismatch);

    if (!v.ok || isMismatch) return;

    setSubmitting(true);
    try {
      await onSubmit(trimmed);
      clear();
    } catch {
      // L'erreur est traitée (toast) par la page parente. On ne touche pas
      // au password ici — on le vide quand même côté défensif.
      clear();
    } finally {
      setSubmitting(false);
    }
  }

  const hasError = errors.length > 0 || mismatch;

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div>
        <label htmlFor={pwdId} className="label">
          {t("auth.password.label")}
        </label>
        <input
          id={pwdId}
          type="password"
          autoComplete="new-password"
          required
          minLength={PASSWORD_MIN_LENGTH}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (errors.length > 0) setErrors([]);
            if (mismatch) setMismatch(false);
          }}
          disabled={disabled || submitting}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorId : helpId}
          className="input"
        />
      </div>

      <div>
        <label htmlFor={confirmId} className="label">
          {t("auth.password.confirm_label")}
        </label>
        <input
          id={confirmId}
          type="password"
          autoComplete="new-password"
          required
          minLength={PASSWORD_MIN_LENGTH}
          value={confirm}
          onChange={(e) => {
            setConfirm(e.target.value);
            if (mismatch) setMismatch(false);
          }}
          disabled={disabled || submitting}
          aria-invalid={mismatch}
          aria-describedby={mismatch ? errorId : helpId}
          className="input"
        />
      </div>

      <p id={helpId} className="text-xs text-slate-500">
        {t("auth.password.requirements", { count: PASSWORD_MIN_LENGTH })}
      </p>

      {hasError && (
        <div
          id={errorId}
          role="alert"
          aria-live="polite"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          <ul className="list-disc pl-4">
            {errors.map((code) => (
              <li key={code}>{t(`errors.password.${code}`)}</li>
            ))}
            {mismatch && <li>{t("errors.password.mismatch")}</li>}
          </ul>
        </div>
      )}

      <button
        type="submit"
        className="btn-primary w-full"
        disabled={disabled || submitting}
      >
        {submitting ? submittingLabel : submitLabel}
      </button>
    </form>
  );
}
